import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CaretDown,
  CaretUp,
  Copy,
  PencilSimple,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  prospectOpportunities as opportunitiesApi,
} from "@/api";
import { formatApiError } from "@/lib/api";
import OpportunityCreateModal from "@/components/OpportunityCreateModal";

// ACTA_PROSPECT_OPPORTUNITIES_VISUAL_COMPACT_V2
// ACTA_PROSPECT_COMPACT_OPPORTUNITIES_PANEL_V1

const STATUS_OPTIONS = [
  ["new", "Nouvelle"],
  ["discussion", "En discussion"],
  ["followup", "À relancer"],
  ["quote_to_create", "Devis à créer"],
  ["quote_sent", "Devis envoyé"],
  ["won", "Gagnée"],
  ["lost", "Perdue"],
];

const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS
);

const STATUS_CLASSES = {
  new:
    "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]",
  discussion:
    "border-[var(--do-info)] bg-[var(--do-info-soft)] text-[var(--do-ink)]",
  followup:
    "border-[var(--do-warning)] bg-[var(--do-warning-soft)] text-[var(--do-ink)]",
  quote_to_create:
    "border-[var(--do-data-orange)] bg-[var(--do-warning-soft)] text-[var(--do-ink)]",
  quote_sent:
    "border-[var(--do-data-sky)] bg-[var(--do-info-soft)] text-[var(--do-ink)]",
  won:
    "border-[var(--do-success)] bg-[var(--do-success-soft)] text-[var(--do-ink)]",
  lost:
    "border-[var(--do-danger)] bg-[var(--do-danger-soft)] text-[var(--do-ink)]",
};

function initialForm(opportunity = null) {
  return {
    title: opportunity?.title || "",
    product_name:
      opportunity?.productName || "",
    estimated_amount:
      opportunity?.estimatedAmount ?? "",
    status:
      opportunity?.status || "new",
    expected_purchase_date:
      opportunity?.expectedPurchaseDate || "",
    next_action:
      opportunity?.nextAction || "",
    next_action_at:
      opportunity?.nextActionAt || "",
    lost_reason:
      opportunity?.lostReason || "",
    comment:
      opportunity?.comment || "",
  };
}

function money(value) {
  const amount = Number(value || 0);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }
  ).format(amount);
}

function dateLabel(value) {
  if (!value) {
    return "Non renseignée";
  }

  try {
    return new Date(
      value
    ).toLocaleDateString(
      "fr-FR",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  } catch {
    return "Non renseignée";
  }
}

function prospectDisplayName(
  prospect = {}
) {
  return (
    prospect.company ||
    [
      prospect.firstName,
      prospect.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    prospect.name ||
    prospect.email ||
    "Contact"
  );
}

function opportunityTitleFromForm(
  form = {},
  prospect = {}
) {
  const product = String(
    form.product_name ||
      form.productName ||
      ""
  ).trim();

  const contact =
    prospectDisplayName(prospect);

  return `${
    product || "Projet commercial"
  } - ${contact}`;
}

export default function ProspectOpportunityPanel({
  prospect,
  onRefresh,
  openCreateForm = false,
}) {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] =
    useState("");
  const [editingId, setEditingId] =
    useState("");
  const [formOpen, setFormOpen] =
    useState(false);
  const [form, setForm] = useState(
    () => initialForm()
  );
  const [loading, setLoading] =
    useState(false);
  const [saving, setSaving] =
    useState(false);

  const prospectId = prospect?.id;

  const summary = useMemo(() => {
    const activeItems = items.filter(
      (item) =>
        !["won", "lost"].includes(
          item.status
        )
    );

    const total = activeItems.reduce(
      (accumulator, item) =>
        accumulator +
        Number(
          item.estimatedAmount || 0
        ),
      0
    );

    return {
      totalCount: items.length,
      activeCount: activeItems.length,
      total,
    };
  }, [items]);

  const load = async () => {
    if (!prospectId) {
      return;
    }

    setLoading(true);

    try {
      const data =
        await opportunitiesApi.list(
          prospectId
        );

      setItems(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Impossible de charger les opportunités."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [prospectId]);

  // ACTA_OPPORTUNITY_FORM_AUTO_OPEN_V1
  useEffect(() => {
    if (
      !openCreateForm ||
      !prospectId
    ) {
      return;
    }

    setEditingId("");
    setForm(initialForm());
    setFormOpen(true);
  }, [
    openCreateForm,
    prospectId,
  ]);

  const updateForm = (
    field,
    value
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const closeForm = () => {
    setEditingId("");
    setForm(initialForm());
    setFormOpen(false);
  };

  const startCreate = () => {
    setEditingId("");
    setForm(initialForm());
    setFormOpen(true);
  };

  const startEdit = (
    opportunity
  ) => {
    setEditingId(opportunity.id);
    setForm(
      initialForm(opportunity)
    );
    setFormOpen(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleExpanded = (
    opportunityId
  ) => {
    setExpandedId(
      (current) =>
        current === opportunityId
          ? ""
          : opportunityId
    );
  };

  const save = async (event) => {
    event.preventDefault();

    if (
      form.status === "lost" &&
      !form.lost_reason.trim()
    ) {
      toast.error(
        "Motif obligatoire pour une opportunité perdue."
      );
      return;
    }

    const payload = {
      ...form,
      title:
        opportunityTitleFromForm(
          form,
          prospect
        ),
    };

    setSaving(true);

    try {
      if (editingId) {
        await opportunitiesApi.update(
          editingId,
          payload
        );

        toast.success(
          "Opportunité mise à jour."
        );
      } else {
        await opportunitiesApi.create(
          prospectId,
          payload
        );

        toast.success(
          "Opportunité enregistrée."
        );
      }

      closeForm();
      await load();

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Enregistrement de l'opportunité impossible."
      );
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (
    opportunity
  ) => {
    setSaving(true);

    try {
      const created =
        await opportunitiesApi.duplicate(
          opportunity.id
        );

      toast.success(
        "Nouvelle opportunité créée."
      );

      setExpandedId(
        created?.id || ""
      );

      await load();

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Duplication impossible."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (
    opportunity
  ) => {
    if (
      !window.confirm(
        `Supprimer l'opportunité ${opportunity.title} ?`
      )
    ) {
      return;
    }

    setSaving(true);

    try {
      await opportunitiesApi.remove(
        opportunity.id
      );

      toast.success(
        "Opportunité supprimée."
      );

      if (
        expandedId === opportunity.id
      ) {
        setExpandedId("");
      }

      await load();

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Suppression impossible."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="au-card overflow-hidden border border-[var(--do-border)]"
      data-testid="prospect-opportunity-panel"
    >
      <header className="border-b border-[var(--do-border)] px-4 py-3.5 sm:px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="label-eyebrow">
              Opportunités
            </div>

            <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
              Opportunités commerciales
            </h2>

            <p className="mt-1 text-sm text-[var(--do-muted)]">
              Une opportunité par machine, prestation ou besoin commercial.
            </p>
          </div>

          <button
            type="button"
            onClick={
              formOpen
                ? closeForm
                : startCreate
            }
            className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-black transition ${
              formOpen
                ? "border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
                : "bg-[var(--do-accent)] text-[var(--do-on-accent)] hover:brightness-110"
            }`}
            data-testid="toggle-opportunity-form"
          >
            {formOpen ? (
              "Fermer"
            ) : (
              <>
                <Plus
                  size={17}
                  weight="bold"
                />
                Créer une opportunité
              </>
            )}
          </button>
        </div>

        <div
          className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3"
          data-testid="opportunity-summary"
        >
          <div className="flex items-center justify-between rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-2 sm:block">
            <div className="text-[11px] font-bold text-[var(--do-muted)]">
              Total
            </div>

            <div className="font-heading text-base font-black text-[var(--do-ink)] sm:mt-0.5">
              {summary.totalCount}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-2 sm:block">
            <div className="text-[11px] font-bold text-[var(--do-muted)]">
              En cours
            </div>

            <div className="font-heading text-base font-black text-[var(--do-ink)] sm:mt-0.5">
              {summary.activeCount}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-2 sm:block">
            <div className="text-[11px] font-bold text-[var(--do-muted)]">
              Montant actif
            </div>

            <div className="font-heading text-base font-black text-[var(--do-ink)] sm:mt-0.5">
              {money(summary.total)}
            </div>
          </div>
        </div>
      </header>

      {formOpen && editingId && (
        <form
          onSubmit={save}
          className="border-b border-[var(--do-border)] bg-[var(--do-bg-2)]/45 px-4 py-5 sm:px-5"
          data-testid="prospect-opportunity-form"
        >
          <div className="mb-4">
            <h3 className="text-base font-black text-[var(--do-ink)]">
              {editingId
                ? "Modifier l’opportunité"
                : "Nouvelle opportunité"}
            </h3>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Produit ou prestation
              </span>

              <input
                value={form.product_name}
                autoFocus
                data-testid="opportunity-product-name"
                onChange={(event) =>
                  updateForm(
                    "product_name",
                    event.target.value
                  )
                }
                placeholder="Ex : maintenance annuelle"
                className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
                required
              />
            </label>

            {editingId && (
              <label className="block">
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Statut
                </span>

                <select
                  value={form.status}
                  onChange={(event) =>
                    updateForm(
                      "status",
                      event.target.value
                    )
                  }
                  className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
                >
                  {STATUS_OPTIONS.map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>
            )}

            <label className="block">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Montant estimé HT
              </span>

              <input
                type="number"
                min="0"
                step="100"
                value={
                  form.estimated_amount
                }
                onChange={(event) =>
                  updateForm(
                    "estimated_amount",
                    event.target.value
                  )
                }
                className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
              />
            </label>

            <label className="block">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Achat estimé
              </span>

              <input
                type="date"
                value={
                  form.expected_purchase_date
                }
                onChange={(event) =>
                  updateForm(
                    "expected_purchase_date",
                    event.target.value
                  )
                }
                className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
              />
            </label>

            <label className="block">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Date de relance
              </span>

              <input
                type="date"
                value={
                  form.next_action_at
                }
                onChange={(event) =>
                  updateForm(
                    "next_action_at",
                    event.target.value
                  )
                }
                className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Prochaine action
              </span>

              <input
                value={form.next_action}
                onChange={(event) =>
                  updateForm(
                    "next_action",
                    event.target.value
                  )
                }
                placeholder="Ex : rappeler, envoyer la brochure, préparer le devis"
                className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
              />
            </label>

            {form.status === "lost" && (
              <label className="block md:col-span-2">
                <span className="text-xs font-bold text-[var(--do-accent)]">
                  Pourquoi cette opportunité est perdue ? *
                </span>

                <textarea
                  required
                  rows={3}
                  value={form.lost_reason}
                  onChange={(event) =>
                    updateForm(
                      "lost_reason",
                      event.target.value
                    )
                  }
                  placeholder="Budget, concurrent, projet annulé..."
                  className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
                  data-testid="prospect-opportunity-lost-reason"
                />
              </label>
            )}

            <label className="block md:col-span-2">
              <span className="text-xs font-bold text-[var(--do-muted)]">
                Commentaire
              </span>

              <textarea
                rows={3}
                value={form.comment}
                onChange={(event) =>
                  updateForm(
                    "comment",
                    event.target.value
                  )
                }
                className="mt-1.5 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm text-[var(--do-ink)]"
              />
            </label>
          </div>

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2.5 text-sm font-bold text-[var(--do-ink)]"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-[var(--do-accent)] px-4 py-2.5 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-60"
              data-testid="prospect-opportunity-save"
            >
              {editingId
                ? "Enregistrer les modifications"
                : "Créer l’opportunité"}
            </button>
          </div>
        </form>
      )}

      <OpportunityCreateModal
        open={
          formOpen &&
          !editingId
        }
        lockedProspect={prospect}
        onClose={closeForm}
        onCreated={async () => {
          await load();

          if (onRefresh) {
            await onRefresh();
          }
        }}
      />

      <div
        className="grid gap-3 bg-[var(--do-bg-2)]/35 p-3 sm:p-4"
        data-testid="compact-opportunity-list"
      >
        {loading && (
          <div className="px-5 py-8 text-center text-sm text-[var(--do-muted)]">
            Chargement des opportunités…
          </div>
        )}

        {!loading &&
          items.length === 0 && (
            <div className="px-5 py-10 text-center">
              <h3 className="text-base font-black text-[var(--do-ink)]">
                Aucune opportunité
              </h3>

              <p className="mt-1 text-sm text-[var(--do-muted)]">
                Créez la première opportunité liée à ce contact.
              </p>

            </div>
          )}

        {!loading &&
          items.map((item) => {
            const expanded =
              expandedId === item.id;

            const statusClass =
              STATUS_CLASSES[
                item.status
              ] ||
              STATUS_CLASSES.new;

            return (
              <article
                key={item.id}
                className="overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                data-testid="prospect-opportunity-row"
              >
                <button
                  type="button"
                  onClick={() =>
                    toggleExpanded(
                      item.id
                    )
                  }
                  className="grid w-full gap-3 px-4 py-3.5 text-left transition hover:bg-[var(--do-bg-2)]/45 sm:px-5 lg:grid-cols-[minmax(0,1fr)_140px_170px_32px] lg:items-center"
                  aria-expanded={expanded}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-heading text-base font-black text-[var(--do-ink)]">
                        {item.productName ||
                          item.title ||
                          "Opportunité"}
                      </h3>

                      <span
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-black ${statusClass}`}
                      >
                        {STATUS_LABEL[
                          item.status
                        ] ||
                          item.status}
                      </span>
                    </div>

                    <p className="mt-1 truncate text-sm text-[var(--do-muted)]">
                      {item.nextAction
                        ? `Prochaine action : ${item.nextAction}`
                        : "Aucune prochaine action renseignée"}
                    </p>
                  </div>

                  <div>
                    <div className="text-[11px] font-bold text-[var(--do-muted)]">
                      Montant estimé
                    </div>

                    <div className="font-heading text-base font-black text-[var(--do-ink)] sm:mt-0.5">
                      {money(
                        item.estimatedAmount
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-bold text-[var(--do-muted)]">
                      Achat estimé
                    </div>

                    <div className="mt-0.5 text-sm font-bold text-[var(--do-ink)]">
                      {dateLabel(
                        item.expectedPurchaseDate
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end text-[var(--do-muted)]">
                    {expanded ? (
                      <CaretUp
                        size={18}
                        weight="bold"
                      />
                    ) : (
                      <CaretDown
                        size={18}
                        weight="bold"
                      />
                    )}
                  </div>
                </button>

                {expanded && (
                  <div
                    className="border-t border-[var(--do-border)] bg-[var(--do-bg-2)]/35 px-4 py-4 sm:px-5"
                    data-testid="expanded-opportunity-details"
                  >
                    <div className="grid gap-4 lg:grid-cols-3">
                      <div>
                        <div className="text-[11px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                          Prochaine action
                        </div>

                        <div className="mt-1 text-sm font-semibold text-[var(--do-ink)]">
                          {item.nextAction ||
                            "Non renseignée"}
                        </div>

                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          {dateLabel(
                            item.nextActionAt
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="text-[11px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                          Commentaire
                        </div>

                        <div className="mt-1 whitespace-pre-wrap text-sm text-[var(--do-ink)]">
                          {item.comment ||
                            "Aucun commentaire"}
                        </div>
                      </div>

                      <div>
                        <div className="text-[11px] font-black uppercase tracking-wide text-[var(--do-muted)]">
                          Créée par
                        </div>

                        <div className="mt-1 text-sm font-semibold text-[var(--do-ink)]">
                          {item.createdByName ||
                            "Utilisateur"}
                        </div>
                      </div>
                    </div>

                    {item.status ===
                      "lost" && (
                      <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-3">
                        <div className="text-xs font-black text-[var(--do-accent)]">
                          Motif de perte
                        </div>

                        <div className="mt-1 whitespace-pre-wrap text-sm text-[var(--do-ink)]">
                          {item.lostReason ||
                            "Non renseigné"}
                        </div>
                      </div>
                    )}

                    {Array.isArray(
                      item.history
                    ) &&
                      item.history.length >
                        0 && (
                        <details className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
                          <summary className="cursor-pointer px-3 py-2.5 text-xs font-black text-[var(--do-ink)]">
                            Historique de l’opportunité
                          </summary>

                          <div className="border-t border-[var(--do-border)] p-3">
                            <div className="space-y-2">
                              {item.history
                                .slice()
                                .reverse()
                                .map(
                                  (
                                    event,
                                    index
                                  ) => (
                                    <div
                                      key={`${
                                        event.at ||
                                        index
                                      }-${
                                        event.type
                                      }`}
                                      className="text-xs text-[var(--do-muted)]"
                                    >
                                      <span className="font-black text-[var(--do-ink)]">
                                        {event.type}
                                      </span>

                                      {event.reason
                                        ? ` — ${event.reason}`
                                        : ""}

                                      <span className="ml-2">
                                        {dateLabel(
                                          event.at
                                        )}
                                        {" · "}
                                        {event.byName ||
                                          "Utilisateur"}
                                      </span>
                                    </div>
                                  )
                                )}
                            </div>
                          </div>
                        </details>
                      )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          startEdit(item)
                        }
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)]"
                      >
                        <PencilSimple
                          size={14}
                        />
                        Modifier
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          duplicate(item)
                        }
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)] disabled:opacity-60"
                      >
                        <Copy size={14} />
                        Dupliquer
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          remove(item)
                        }
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs font-black text-[var(--do-accent)] disabled:opacity-60"
                      >
                        <Trash size={14} />
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
      </div>
    </section>
  );
}
